import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import { Wallet2, User, Settings, LogOut, TrendingUp, ChevronDown } from "lucide-react";
import logoImg from "../assets/img/swiftex1.png";

/* Shares the same token system as the Wallet redesign (--void, --panel, */
/* --amber, --teal, --rose, IBM Plex Mono / Manrope) so both feel like  */
/* one product.                                                         */

const NAV_LINKS = [
  { to: "/", label: "Home", exact: true },
  { to: "/exchange", label: "Exchange" },
  { to: "/arbitrage", label: "Arbitrage" },
];

export default function Header() {
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [btcPrice, setBtcPrice] = useState(null);
  const [logoBroken, setLogoBroken] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const menuRef = useRef(null);
  const mobileRef = useRef(null);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const res = await axios.get("https://swiftexprofit.com/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data && res.data.user) setUser(res.data.user);
    } catch (err) {
      console.error("Failed to fetch user profile:", err);
      setUser(null);
    }
  }, []);

  const fetchBTCPrice = useCallback(async () => {
    try {
      const res = await axios.get("https://swiftexprofit.com/price/btc");
      if (res.data && res.data.btc_usd) setBtcPrice(res.data.btc_usd);
    } catch (err) {
      console.error("Failed to fetch BTC price:", err);
      setBtcPrice(null);
    }
  }, []);

  useEffect(() => {
    document.body.classList.add("dark");
    fetchUser();
    fetchBTCPrice();

    const handleFocus = () => {
      fetchUser();
      fetchBTCPrice();
    };
    window.addEventListener("storage", handleFocus);
    window.addEventListener("focus", handleFocus);

    const interval = setInterval(() => {
      fetchUser();
      fetchBTCPrice();
    }, 30000);

    return () => {
      window.removeEventListener("storage", handleFocus);
      window.removeEventListener("focus", handleFocus);
      clearInterval(interval);
    };
  }, [fetchUser, fetchBTCPrice]);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
  }, [location]);

  // Close user dropdown on outside click / Escape
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Lock body scroll while the mobile panel is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setMenuOpen(false);
    setMobileOpen(false);
    history.push("/login");
  };

  const isActive = (to, exact) => (exact ? location.pathname === to : location.pathname.startsWith(to));

  const usdBalance = (() => {
    if (!user || !user.balance || !btcPrice) return null;
    const total = parseFloat(user.balance) * btcPrice;
    return isNaN(total) ? null : total;
  })();

  const initials = user?.username ? user.username.slice(0, 2).toUpperCase() : "";

  return (
    <header className="hdr-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@500;600;700&family=Manrope:wght@400;500;600;700;800&display=swap');

        .hdr-root {
          --void: #0B0E14;
          --panel: #12161F;
          --panel-2: #171C27;
          --line: #232838;
          --text: #F3EFE6;
          --text-dim: #8891A3;
          --amber: #10b981;
          --amber-soft: rgba(242,169,60,0.12);
          --teal: #35C7A0;
          --teal-soft: rgba(53,199,160,0.12);
          --rose: #E8626B;
          --rose-soft: rgba(232,98,107,0.12);
          font-family: 'Manrope', -apple-system, sans-serif;
          position: sticky;
          top: 0;
          z-index: 80;
        }
        .hdr-root *, .hdr-root *::before, .hdr-root *::after { box-sizing: border-box; }
        .hdr-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; }

        .hdr-bar {
          background: rgba(18,22,31,0.86);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--line);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          height: 64px;
        }

        .hdr-left { display: flex; align-items: center; gap: 32px; min-width: 0; }
        .hdr-logo { display: flex; align-items: center; gap: 8px; flex-shrink: 0; text-decoration: none; }
        .hdr-logo img { height: 100px; display: block; }
        .hdr-logo-fallback { font-weight: 800; font-size: 17px; color: var(--text); letter-spacing: -0.01em; }
        .hdr-logo-fallback .accent { color: var(--amber); }

        .hdr-nav { display: none; align-items: center; gap: 4px; }
        .hdr-link {
          position: relative;
          color: var(--text-dim);
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
          padding: 8px 14px;
          border-radius: 8px;
          transition: color 0.12s ease, background 0.12s ease;
        }
        .hdr-link:hover { color: var(--text); background: var(--panel-2); }
        .hdr-link.active { color: var(--amber); }
        .hdr-link.active::after {
          content: '';
          position: absolute;
          left: 14px; right: 14px; bottom: 2px;
          height: 2px;
          background: var(--amber);
          border-radius: 2px;
        }

        .hdr-right { display: flex; align-items: center; gap: 12px; }

        .hdr-price-chip {
          display: none;
          align-items: center;
          gap: 6px;
          background: var(--teal-soft);
          color: var(--teal);
          border: 1px solid rgba(53,199,160,0.3);
          border-radius: 999px;
          padding: 6px 12px;
          font-size: 12.5px;
          font-weight: 700;
          white-space: nowrap;
        }
        .hdr-price-chip .hdr-mono { color: var(--text); }

        .hdr-login-btn {
          background: var(--amber);
          color: #211505;
          text-decoration: none;
          font-size: 13.5px;
          font-weight: 700;
          padding: 9px 18px;
          border-radius: 10px;
          transition: filter 0.12s ease;
        }
        .hdr-login-btn:hover { filter: brightness(1.08); }
        .hdr-login-btn:focus-visible { outline: 2px solid var(--amber); outline-offset: 2px; }

        .hdr-avatar-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--panel-2);
          border: 1px solid var(--line);
          border-radius: 999px;
          padding: 5px 12px 5px 5px;
          cursor: pointer;
          color: var(--text);
          font-family: inherit;
        }
        .hdr-avatar-btn:hover { border-color: var(--amber); }
        .hdr-avatar-btn:focus-visible { outline: 2px solid var(--amber); outline-offset: 2px; }
        .hdr-avatar-circle {
          width: 28px; height: 28px;
          border-radius: 50%;
          background: var(--amber-soft);
          color: var(--amber);
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800;
          flex-shrink: 0;
        }
        .hdr-avatar-name { font-size: 13px; font-weight: 700; display: none; }
        .hdr-avatar-btn svg { color: var(--text-dim); flex-shrink: 0; }

        .hdr-menu-wrap { position: relative; }
        .hdr-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 268px;
          background: var(--panel);
          border: 1px solid var(--line);
          border-radius: 16px;
          padding: 8px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.45);
          animation: hdr-pop 0.15s cubic-bezier(0.2,0.8,0.2,1);
        }
        @keyframes hdr-pop { from { opacity: 0; transform: translateY(-6px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }

        .hdr-dropdown-head { padding: 16px 14px 14px; text-align: center; border-bottom: 1px solid var(--line); margin-bottom: 8px; }
        .hdr-dropdown-avatar {
          width: 48px; height: 48px; border-radius: 50%;
          background: var(--amber-soft); color: var(--amber);
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; font-size: 16px;
          margin: 0 auto 10px;
        }
        .hdr-dropdown-name { font-weight: 700; font-size: 14px; margin: 0; }
        .hdr-dropdown-email { color: var(--text-dim); font-size: 12.5px; margin: 2px 0 10px; word-break: break-all; }
        .hdr-dropdown-balance { font-family: 'IBM Plex Mono', monospace; font-weight: 700; font-size: 17px; color: var(--teal); }
        .hdr-dropdown-balance-loading { font-size: 12.5px; color: var(--text-dim); }

        .hdr-menu-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 12px;
          border-radius: 10px;
          font-size: 13.5px;
          font-weight: 600;
          color: var(--text);
          text-decoration: none;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: inherit;
        }
        .hdr-menu-item:hover { background: var(--panel-2); }
        .hdr-menu-item svg { color: var(--text-dim); flex-shrink: 0; }
        .hdr-menu-item.danger { color: var(--rose); }
        .hdr-menu-item.danger svg { color: var(--rose); }

        /* Hamburger */
        .hdr-burger {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 5px;
          width: 38px; height: 38px;
          border-radius: 10px;
          background: var(--panel-2);
          border: 1px solid var(--line);
          cursor: pointer;
          padding: 0;
          align-items: center;
        }
        .hdr-burger:focus-visible { outline: 2px solid var(--amber); outline-offset: 2px; }
        .hdr-burger span {
          display: block;
          width: 16px; height: 2px;
          background: var(--text);
          border-radius: 2px;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }
        .hdr-burger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        .hdr-burger.open span:nth-child(2) { opacity: 0; }
        .hdr-burger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

        /* Mobile panel */
        .hdr-mobile-panel {
          position: fixed;
          top: 64px; left: 0; right: 0;
          bottom: 0;
          background: var(--void);
          border-top: 1px solid var(--line);
          padding: 18px 16px 28px;
          overflow-y: auto;
          animation: hdr-slide 0.18s ease;
        }
        @keyframes hdr-slide { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

        .hdr-mobile-link {
          display: block;
          padding: 14px 12px;
          font-size: 15px;
          font-weight: 700;
          color: var(--text);
          text-decoration: none;
          border-bottom: 1px solid var(--line);
        }
        .hdr-mobile-link.active { color: var(--amber); }

        .hdr-mobile-user {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 12px;
          margin-top: 8px;
          background: var(--panel);
          border: 1px solid var(--line);
          border-radius: 14px;
        }

        .hdr-mobile-price {
          display: flex;
          align-items: center;
          gap: 6px;
          color: var(--teal);
          font-size: 13px;
          font-weight: 700;
          padding: 12px;
        }

        @media (min-width: 960px) {
          .hdr-nav { display: flex; }
          .hdr-burger { display: none; }
          .hdr-avatar-name { display: block; }
          .hdr-price-chip { display: inline-flex; }
        }
      `}</style>

      <div className="hdr-bar">
        <div className="hdr-left">
          <Link className="hdr-logo" to="/">
            {logoBroken ? (
              <span className="hdr-logo-fallback">
                Swift<span className="accent">Ex</span>
              </span>
            ) : (
              <img src={logoImg} alt="SwiftEx" onError={() => setLogoBroken(true)} />
            )}
          </Link>

          <nav className="hdr-nav" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className={`hdr-link ${isActive(link.to, link.exact) ? "active" : ""}`}>
                {link.label}
              </Link>
            ))}
            <Link to={user ? "/wallet" : "/login"} className={`hdr-link ${isActive("/wallet") ? "active" : ""}`}>
              {user ? "Wallet" : "Login"}
            </Link>
          </nav>
        </div>

        <div className="hdr-right">
          <div className="hdr-price-chip">
            <TrendingUp size={13} />
            {btcPrice ? <span className="hdr-mono">${btcPrice.toLocaleString()}</span> : <span>—</span>}
          </div>

          {user ? (
            <div className="hdr-menu-wrap" ref={menuRef}>
              <button className="hdr-avatar-btn" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen} aria-haspopup="true">
                <span className="hdr-avatar-circle">{initials}</span>
                <span className="hdr-avatar-name">{user.username}</span>
                <ChevronDown size={14} />
              </button>

              {menuOpen && (
                <div className="hdr-dropdown" role="menu">
                  <div className="hdr-dropdown-head">
                    <div className="hdr-dropdown-avatar">{initials}</div>
                    <p className="hdr-dropdown-name">{user.username}</p>
                    <p className="hdr-dropdown-email">{user.email}</p>
                    {usdBalance !== null ? (
                      <div className="hdr-dropdown-balance">
                        ${usdBalance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    ) : (
                      <div className="hdr-dropdown-balance-loading">Loading balance…</div>
                    )}
                  </div>

                  <Link to="/profile" className="hdr-menu-item" role="menuitem">
                    <User size={15} /> Profile
                  </Link>
                  <Link to="/wallet" className="hdr-menu-item" role="menuitem">
                    <Wallet2 size={15} /> My wallet
                  </Link>
                  <Link to="/settings" className="hdr-menu-item" role="menuitem">
                    <Settings size={15} /> Settings
                  </Link>
                  <button className="hdr-menu-item danger" onClick={handleLogout} role="menuitem">
                    <LogOut size={15} /> Log out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="hdr-login-btn">
              Log in
            </Link>
          )}

          <button
            className={`hdr-burger ${mobileOpen ? "open" : ""}`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="hdr-mobile-panel" ref={mobileRef}>
          {NAV_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className={`hdr-mobile-link ${isActive(link.to, link.exact) ? "active" : ""}`}>
              {link.label}
            </Link>
          ))}
          <Link to={user ? "/wallet" : "/login"} className={`hdr-mobile-link ${isActive("/wallet") ? "active" : ""}`}>
            {user ? "Wallet" : "Login"}
          </Link>

          {user && (
            <>
              <Link to="/profile" className="hdr-mobile-link">Profile</Link>
              <Link to="/settings" className="hdr-mobile-link">Settings</Link>
            </>
          )}

          <div className="hdr-mobile-price">
            <TrendingUp size={14} />
            {btcPrice ? <span className="hdr-mono">BTC ${btcPrice.toLocaleString()}</span> : "Loading price…"}
          </div>

          {user ? (
            <div className="hdr-mobile-user">
              <span className="hdr-avatar-circle">{initials}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{user.username}</div>
                {usdBalance !== null && (
                  <div className="hdr-mono" style={{ color: "var(--teal)", fontWeight: 700, fontSize: 13 }}>
                    ${usdBalance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                )}
              </div>
              <button className="hdr-menu-item danger" style={{ width: "auto" }} onClick={handleLogout}>
                <LogOut size={15} />
              </button>
            </div>
          ) : (
            <Link to="/login" className="hdr-login-btn" style={{ display: "block", textAlign: "center", marginTop: 12 }}>
              Log in
            </Link>
          )}
        </div>
      )}
    </header>
  );
}